import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { FileText, TrendingUp, Home, MapPin, Check } from 'lucide-react';

const ExposeSections = ({ data }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');

  const tabIcons = {
    overview: FileText,
    economics: TrendingUp,
    development: Home,
    location: MapPin,
    floorplan: Home
  };

  // Translate tab titles
  const translateTitle = (id, originalTitle) => {
    const titleMap = {
      'overview': t('expose.tabs.overview'),
      'economics': t('expose.tabs.economics'),
      'development': t('expose.tabs.development'),
      'location': t('expose.tabs.location'),
      'floorplan': t('expose.tabs.floorplan')
    };
    return titleMap[id] || originalTitle;
  };

  return (
    <div className="max-w-6xl mx-auto">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-2 md:grid-cols-5 w-full bg-white border border-slate-200 p-1 rounded-lg">
          {data.sections.map((section) => {
            const Icon = tabIcons[section.id];
            return (
              <TabsTrigger 
                key={section.id} 
                value={section.id}
                className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white"
              >
                <Icon className="w-4 h-4 mr-2" />
                <span className="hidden md:inline">{translateTitle(section.id, section.title)}</span>
                <span className="md:hidden">{translateTitle(section.id, section.title).split(' ')[0]}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {data.sections.map((section) => (
          <TabsContent key={section.id} value={section.id} className="mt-6">
            <Card className="border-slate-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
                <CardTitle className="text-2xl text-[#0A2540] flex items-center">
                  {React.createElement(tabIcons[section.id], { className: "w-6 h-6 mr-3 text-[#C9A961]" })}
                  {translateTitle(section.id, section.title)}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                {/* Overview Section */}
                {section.id === 'overview' && (
                  <div className="space-y-8">
                    {section.content.text && (
                      <p className="text-lg text-slate-700 leading-relaxed mb-6">{section.content.text}</p>
                    )}
                    
                    {section.content.grunddaten && (
                      <div>
                        <h4 className="text-xl font-bold text-[#0A2540] mb-4">{t('expose.grunddaten')}</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          {section.content.grunddaten.map((item, idx) => (
                            <div key={idx} className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                              <div className="w-2 h-2 rounded-full bg-[#C9A961] mt-2 flex-shrink-0"></div>
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-slate-600 mb-1">{item.label}</p>
                                <p className="text-base text-[#0A2540] font-medium">{item.value}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {section.content.hoteldetails && (
                      <div>
                        <h4 className="text-xl font-bold text-[#0A2540] mb-4">{t('expose.hotelDetails')}</h4>
                        <div className="grid md:grid-cols-2 gap-3">
                          {section.content.hoteldetails.map((detail, idx) => (
                            <div key={idx} className="flex items-start space-x-2 p-3 bg-white border border-[#C9A961]/30 rounded-lg">
                              <Check className="w-5 h-5 text-[#C9A961] flex-shrink-0 mt-0.5" />
                              <p className="text-slate-700">{detail}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {section.content.villadetails && (
                      <div>
                        <h4 className="text-xl font-bold text-[#0A2540] mb-4">{t('expose.villaDetails')}</h4>
                        <div className="grid md:grid-cols-2 gap-3">
                          {section.content.villadetails.map((detail, idx) => (
                            <div key={idx} className="flex items-start space-x-2 p-3 bg-white border border-[#2C5F66]/30 rounded-lg">
                              <Check className="w-5 h-5 text-[#2C5F66] flex-shrink-0 mt-0.5" />
                              <p className="text-slate-700">{detail}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Restaurant section removed - no longer part of property offering */}
                  </div>
                )}

                {/* Economics Section */}
                {section.id === 'economics' && (
                  <div className="space-y-8">
                    {section.content.text && (
                      <p className="text-lg text-slate-700 leading-relaxed">{section.content.text}</p>
                    )}
                    
                    {section.content.grundlagen && (
                      <div>
                        <h4 className="text-xl font-bold text-[#0A2540] mb-4">{t('expose.planungsgrundlagen')}</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          {section.content.grundlagen.map((item, idx) => (
                            <div key={idx} className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                              <p className="text-sm text-slate-600 mb-1">{item.label}</p>
                              <p className="text-base font-semibold text-[#0A2540]">{item.value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {section.content.umsatzplanung && (
                      <div>
                        <h4 className="text-xl font-bold text-[#0A2540] mb-4">{t('expose.umsatzplanung')}</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="bg-[#0A2540] text-white">
                                <th className="p-3 text-left">{t('expose.tableHeaders.jahr')}</th>
                                {section.content.umsatzplanung[0]?.belegt && <th className="p-3 text-right">{t('expose.tableHeaders.belegt')}</th>}
                                {section.content.umsatzplanung[0]?.gaeste && <th className="p-3 text-right">{t('expose.tableHeaders.gaeste')}</th>}
                                {section.content.umsatzplanung[0]?.zimmer && <th className="p-3 text-right">{t('expose.tableHeaders.zimmer')}</th>}
                                {section.content.umsatzplanung[0]?.gastro && <th className="p-3 text-right">{t('expose.tableHeaders.gastro')}</th>}
                                {section.content.umsatzplanung[0]?.fb && <th className="p-3 text-right">{t('expose.tableHeaders.fb')}</th>}
                                {section.content.umsatzplanung[0]?.events && <th className="p-3 text-right">{t('expose.tableHeaders.events')}</th>}
                                {section.content.umsatzplanung[0]?.event && <th className="p-3 text-right">{t('expose.tableHeaders.event')}</th>}
                                <th className="p-3 text-right">{t('expose.tableHeaders.gesamt')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {section.content.umsatzplanung.map((row, idx) => (
                                <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50">
                                  <td className="p-3 font-semibold">{row.jahr}</td>
                                  {row.belegt && <td className="p-3 text-right">{row.belegt}</td>}
                                  {row.gaeste && <td className="p-3 text-right">{row.gaeste}</td>}
                                  {row.zimmer && <td className="p-3 text-right">{row.zimmer}</td>}
                                  {row.gastro && <td className="p-3 text-right">{row.gastro}</td>}
                                  {row.fb && <td className="p-3 text-right">{row.fb}</td>}
                                  {row.events && <td className="p-3 text-right">{row.events}</td>}
                                  {row.event && <td className="p-3 text-right">{row.event}</td>}
                                  <td className="p-3 text-right font-bold text-[#C9A961]">{row.gesamt}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {(section.content.ergebnis_jahr3 || section.content.kostenstruktur_jahr3) && (
                      <div>
                        <h4 className="text-xl font-bold text-[#0A2540] mb-4">{t('expose.kostenstruktur')}</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          {(section.content.ergebnis_jahr3 || section.content.kostenstruktur_jahr3).map((item, idx) => (
                            <div key={idx} className="p-4 bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-lg">
                              <p className="text-sm text-slate-600 mb-1">{item.label}</p>
                              <p className="text-lg font-bold text-[#0A2540]">{item.value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {section.content.finanzierung && (
                      <div>
                        <h4 className="text-xl font-bold text-[#0A2540] mb-4">{t('expose.finanzierung')}</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          {section.content.finanzierung.map((item, idx) => (
                            <div key={idx} className="p-4 bg-gradient-to-br from-[#C9A961]/10 to-[#D4AF37]/10 border border-[#C9A961] rounded-lg">
                              <p className="text-sm text-slate-600 mb-1">{item.label}</p>
                              <p className="text-lg font-bold text-[#0A2540]">{item.value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {section.content.kapitaldienstfaehigkeit && (
                      <div>
                        <h4 className="text-xl font-bold text-[#0A2540] mb-4">{t('expose.kapitaldienstfaehigkeit')}</h4>
                        <div className="grid md:grid-cols-3 gap-4">
                          {section.content.kapitaldienstfaehigkeit.map((item, idx) => (
                            <div key={idx} className="p-5 bg-gradient-to-br from-[#C9A961]/10 to-[#D4AF37]/10 border-2 border-[#C9A961] rounded-lg">
                              <p className="text-sm text-slate-600 mb-2">{item.jahr}</p>
                              <p className="text-base text-slate-700 mb-1">EBITDA: {item.ebitda}</p>
                              <p className="text-base text-slate-700 mb-1">Kapitaldienst: {item.kapitaldienst}</p>
                              <p className="text-2xl font-bold text-[#0A2540]">DSCR: {item.dscr}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {section.content.breakeven && (
                      <div>
                        <h4 className="text-xl font-bold text-[#0A2540] mb-4">{t('expose.breakeven')}</h4>
                        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
                          {section.content.breakeven.map((item, idx) => (
                            <div key={idx} className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-center">
                              <p className="text-xs text-slate-600 mb-2">{item.label}</p>
                              <p className="text-xl font-bold text-[#2C5F66]">{item.value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {(section.content.kennzahlen_jahr3 || section.content.kennzahlen) && (
                      <div>
                        <h4 className="text-xl font-bold text-[#0A2540] mb-4">{t('expose.kennzahlen')}</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          {(section.content.kennzahlen_jahr3 || section.content.kennzahlen).map((item, idx) => (
                            <div key={idx} className="p-4 bg-white border border-[#C9A961] rounded-lg">
                              <p className="text-sm text-slate-600 mb-1">{item.label}</p>
                              <p className="text-lg font-bold text-[#0A2540]">{item.value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {section.content.sensitivitaet && (
                      <div>
                        <h4 className="text-xl font-bold text-[#0A2540] mb-4">{t('expose.sensitivitaet')}</h4>
                        <div className="overflow-x-auto bg-white border-2 border-slate-200 rounded-lg">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-gradient-to-r from-[#0A2540] to-[#2C5F66] text-white">
                                <th className="p-4 text-left">{t('expose.tableHeaders.belegt')} / ADR</th>
                                <th className="p-4 text-right">150€</th>
                                <th className="p-4 text-right">160€</th>
                                <th className="p-4 text-right">165€</th>
                                <th className="p-4 text-right">170€</th>
                              </tr>
                            </thead>
                            <tbody>
                              {section.content.sensitivitaet.map((row, idx) => (
                                <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50">
                                  <td className="p-4 font-semibold">{row.auslastung}</td>
                                  <td className="p-4 text-right">{row.adr150}</td>
                                  <td className="p-4 text-right">{row.adr160}</td>
                                  <td className="p-4 text-right font-bold text-[#C9A961]">{row.adr165}</td>
                                  <td className="p-4 text-right">{row.adr170}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="text-sm text-slate-600 mt-3 italic">
                          {t('expose.sensitivitaetNote')}
                        </p>
                      </div>
                    )}
                    
                    {section.content.note && (
                      <div className="bg-green-50 border-2 border-green-300 rounded-lg p-5">
                        <p className="text-sm font-medium text-green-900 leading-relaxed">{section.content.note}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Development Section */}
                {section.id === 'development' && section.content.text && (
                  <div>
                    <p className="text-lg text-slate-700 mb-6 leading-relaxed">{section.content.text}</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      {section.content.potenziale?.map((potential, idx) => (
                        <div key={idx} className="flex items-start space-x-3 p-4 bg-gradient-to-br from-[#C9A961]/5 to-[#D4AF37]/5 border border-[#C9A961]/20 rounded-lg">
                          <div className="w-6 h-6 rounded-full bg-[#C9A961] flex items-center justify-center flex-shrink-0">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                          <p className="text-slate-700 font-medium">{potential}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Location Section */}
                {section.id === 'location' && section.content.highlights && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {section.content.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-start space-x-3 p-5 bg-slate-50 border border-slate-200 rounded-lg hover:border-[#2C5F66] transition-colors">
                        <div className="w-6 h-6 rounded-full bg-[#2C5F66] flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-4 h-4 text-white" />
                        </div>
                        <p className="text-slate-700 font-medium">{highlight}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Floorplan Section */}
                {section.id === 'floorplan' && (
                  <div className="space-y-6">
                    {section.content.text && (
                      <p className="text-lg text-slate-700 leading-relaxed mb-6">{section.content.text}</p>
                    )}
                    
                    {section.content.image && (
                      <div className="bg-slate-900 border-2 border-[#C9A961] rounded-xl overflow-hidden p-4">
                        <img 
                          src={section.content.image} 
                          alt={t('expose.grundrissAlt')}
                          className="w-full h-auto rounded-lg"
                        />
                      </div>
                    )}
                    
                    {section.content.features && (
                      <div>
                        <h4 className="text-xl font-bold text-[#0A2540] mb-4 mt-8">{t('expose.entwicklungsplanung')}</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          {section.content.features.map((feature, idx) => (
                            <div key={idx} className="flex items-start space-x-3 p-4 bg-gradient-to-br from-[#C9A961]/5 to-[#D4AF37]/5 border border-[#C9A961]/20 rounded-lg">
                              <div className="w-6 h-6 rounded-full bg-[#C9A961] flex items-center justify-center flex-shrink-0">
                                <Check className="w-4 h-4 text-white" />
                              </div>
                              <p className="text-slate-700 font-medium">{feature}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default ExposeSections;